import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Video, X } from 'lucide-react';
import { resolveRpEmbed } from '@mlm/shared';
import { cn } from '@mlm/ui';
import { EmptyState } from '../components/empty-state';
import {
  listTrainingVideos,
  trainingVideoThumbnailUrl,
  type TrainingVideo,
} from '../lib/api';

export function TrainingVideosPage() {
  const [category, setCategory] = useState<string>('');
  const [playing, setPlaying] = useState<TrainingVideo | null>(null);
  const query = useQuery({
    queryKey: ['training-videos', category],
    queryFn: () => listTrainingVideos(category || undefined),
  });

  const categories = query.data?.categories ?? [];
  const videos = query.data?.videos ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="flex items-center gap-2 text-h1">
          <Video className="h-6 w-6 text-muted-foreground" /> Training Videos
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Guides and tutorials to help you build your team.
        </p>
      </div>

      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <FilterChip label="All" active={category === ''} onClick={() => setCategory('')} />
          {categories.map((c) => (
            <FilterChip
              key={c.id}
              label={c.name}
              active={category === c.id}
              onClick={() => setCategory(c.id)}
            />
          ))}
        </div>
      )}

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : videos.length === 0 ? (
        <EmptyState
          icon={Video}
          title="No videos yet"
          description="Your training library is on the way. When videos are published, they'll appear here."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {videos.map((v) => (
            <button
              key={v.id}
              onClick={() => setPlaying(v)}
              className="group overflow-hidden rounded-lg border bg-card text-left transition-shadow hover:shadow-md"
            >
              <div className="relative aspect-video w-full bg-muted">
                {v.thumbnailKey ? (
                  <img
                    src={trainingVideoThumbnailUrl(v.id)}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <Video className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}
                <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/20">
                  <div className="rounded-full bg-white/90 p-3 opacity-0 transition-opacity group-hover:opacity-100">
                    <Video className="h-5 w-5 text-black" />
                  </div>
                </div>
              </div>
              <div className="p-3">
                <p className="font-medium leading-tight">{v.title}</p>
                {v.description && (
                  <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{v.description}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {playing && <PlayerModal video={playing} onClose={() => setPlaying(null)} />}
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-1 text-sm transition-colors',
        active
          ? 'border-primary bg-primary/10 text-primary font-medium'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
}

function PlayerModal({ video, onClose }: { video: TrainingVideo; onClose: () => void }) {
  const embed = resolveRpEmbed(video.videoUrl);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl overflow-hidden rounded-lg bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-4 py-2">
          <p className="font-medium">{video.title}</p>
          <button onClick={onClose} aria-label="Close" className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="aspect-video w-full bg-black">
          {embed.kind === 'video' ? (
            <video src={embed.src} controls autoPlay className="h-full w-full" />
          ) : (
            <iframe
              src={embed.src}
              title={video.title}
              className="h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              // Vimeo's domain-lock privacy check reads this request's REFERER.
              referrerPolicy="strict-origin-when-cross-origin"
            />
          )}
        </div>
        {video.description && (
          <p className="px-4 py-3 text-sm text-muted-foreground">{video.description}</p>
        )}
      </div>
    </div>
  );
}
